import React, { useEffect } from 'react';
import { motion, useReducedMotion, useSpring, useTransform, useVelocity } from 'framer-motion';
import { cn } from '@/lib/utils';

/*
 * The clock as an odometer, after Bencho's Rolling counter block: every digit
 * is a strip of numerals behind a window, and a change rolls the strip.
 *
 * A column follows a COUNT, not its digit: the seconds-tens column follows
 * how many whole tens of seconds are left, the minutes column how many whole
 * minutes. So 1:00 -> 0:59 moves each column one step the way time runs and
 * never spins a strip back through every numeral to wrap. Seconds tens count
 * in sixes. Each column springs on its own and a beat after the one to its
 * right, so a carry ripples left, and a fast roll smears slightly.
 */

const STAGGER_MS = 45;

function Column({ count, base, index }: { count: number; base: number; index: number }) {
  const still = useReducedMotion();
  const spring = useSpring(count, { stiffness: 340, damping: 24, mass: 0.6 });
  const velocity = useVelocity(spring);

  useEffect(() => {
    if (still) {
      spring.jump(count);
      return;
    }
    const t = setTimeout(() => {
      // A big jump (adding a minute moves the seconds count by 60) skips whole
      // turns first, which look identical, so only the last step rolls.
      const turns = Math.trunc((count - spring.get()) / base);
      if (turns) spring.jump(spring.get() + turns * base);
      spring.set(count);
    }, index * STAGGER_MS);
    return () => clearTimeout(t);
  }, [count, base, index, spring, still]);

  // Where the strip sits: the count taken round the base. The strip ends on a
  // repeat of its first numeral, so going past the top is seamless.
  const y = useTransform(spring, (v) => `${-(((v % base) + base) % base)}em`);
  const filter = useTransform(velocity, (v) => {
    const smear = Math.min(2.5, Math.abs(v) / 10);
    return smear > 0.2 ? `blur(${smear.toFixed(2)}px)` : 'none';
  });

  return (
    <span className="relative inline-block h-[1em] overflow-hidden" aria-hidden>
      {/* Sizes the window to one tabular numeral */}
      <span className="invisible">0</span>
      <motion.span className="absolute left-0 top-0 flex flex-col" style={{ y, filter }}>
        {Array.from({ length: base + 1 }, (_, i) => (
          <span key={i} className="block h-[1em]">
            {i % base}
          </span>
        ))}
      </motion.span>
    </span>
  );
}

/** "25:00", "1:05:09" or "+02:13", with every digit on a rolling strip. */
export function RollingClock({ ms, className }: { ms: number; className?: string }) {
  const over = ms < 0;
  // Whole seconds the same way formatClock counts them
  const total = over ? Math.floor(-ms / 1000) : Math.ceil(ms / 1000);
  const hours = Math.floor(total / 3600);

  // Right to left, so index is the stagger order
  const columns: Array<{ count: number; base: number } | ':'> = [
    { count: total, base: 10 },
    { count: Math.floor(total / 10), base: 6 },
    ':',
    { count: Math.floor(total / 60), base: 10 },
    { count: Math.floor(total / 600), base: 6 },
  ];
  if (hours > 0) columns.push(':', { count: hours, base: 10 });

  let index = 0;
  const cells = columns.map((c, i) =>
    c === ':' ? (
      <span key={i} aria-hidden>
        :
      </span>
    ) : (
      <Column key={i} count={c.count} base={c.base} index={index++} />
    ),
  );

  const label = `${over ? '+' : ''}${hours > 0 ? `${hours}:` : ''}${String(Math.floor((total % 3600) / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;

  return (
    <span
      className={cn(
        'inline-flex items-center tabular-nums',
        // A soft fade at the top and bottom of the windows, like a printed drum
        '[mask-image:linear-gradient(to_bottom,transparent,black_10%,black_90%,transparent)]',
        className,
      )}
      // Inline, not a class: tailwind-merge drops leading-* when a caller passes a text size,
      // and the strips only line up with their windows at exactly 1em per numeral
      style={{ lineHeight: 1 }}
      role="text"
      aria-label={label}
    >
      {over && <span aria-hidden>+</span>}
      {cells.reverse()}
    </span>
  );
}
